import { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, Volume2, BookOpen, Table } from 'lucide-react';
import { speak } from './api';

// Verbes utiles pour parler et former des phrases — AVEC exercice
const USEFUL = [
  { verb: 'to be (être) — présent', rows: [['I', 'am'], ['you', 'are'], ['he / she / it', 'is'], ['we', 'are'], ['they', 'are']], note: "Contractions : I'm, you're, he's, we're, they're." },
  { verb: 'to have (avoir) — présent', rows: [['I', 'have'], ['you', 'have'], ['he / she / it', 'has'], ['we', 'have'], ['they', 'have']], note: 'Seule la 3e personne du singulier change : he/she/it HAS.' },
  { verb: 'Présent simple (to work) — habitudes', rows: [['I / you / we / they', 'work'], ['he / she / it', 'works']], note: 'On ajoute -s à la 3e personne du singulier.' },
  { verb: 'Passé simple (to be)', rows: [['I / he / she / it', 'was'], ['you / we / they', 'were']], note: 'Les autres verbes : verbe régulier + -ed, ou forme irrégulière (voir tableau).' },
  { verb: 'Futur (will + base)', rows: [['I / you / he / she / it', 'will go'], ['we / you / they', 'will go']], note: 'Invariable : will + verbe à la base.' },
];

// Verbes irréguliers — TABLEAU DE RÉFÉRENCE (présent · passé · futur) — SANS exercice
const IRREGULAR = [
  ['be (être)', 'is / are', 'was / were', 'will be'],
  ['have (avoir)', 'has', 'had', 'will have'],
  ['do (faire)', 'does', 'did', 'will do'],
  ['go (aller)', 'goes', 'went', 'will go'],
  ['make (fabriquer)', 'makes', 'made', 'will make'],
  ['take (prendre)', 'takes', 'took', 'will take'],
  ['come (venir)', 'comes', 'came', 'will come'],
  ['see (voir)', 'sees', 'saw', 'will see'],
  ['get (obtenir)', 'gets', 'got', 'will get'],
  ['give (donner)', 'gives', 'gave', 'will give'],
  ['know (savoir)', 'knows', 'knew', 'will know'],
  ['think (penser)', 'thinks', 'thought', 'will think'],
  ['say (dire)', 'says', 'said', 'will say'],
  ['eat (manger)', 'eats', 'ate', 'will eat'],
  ['drink (boire)', 'drinks', 'drank', 'will drink'],
  ['speak (parler)', 'speaks', 'spoke', 'will speak'],
  ['write (écrire)', 'writes', 'wrote', 'will write'],
  ['read (lire)', 'reads', 'read', 'will read'],
  ['put (poser)', 'puts', 'put', 'will put'],
  ['find (trouver)', 'finds', 'found', 'will find'],
];

function VerbQuiz({ table, idx, lang }) {
  const rows = table.rows;
  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState({});
  const [checked, setChecked] = useState(false);
  const norm = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const isRight = (k) => {
    const expected = norm(rows[k][1]);
    const got = norm(answers[k]);
    return expected.split('/').map((x) => x.trim()).includes(got) || got === expected;
  };
  const score = rows.filter((_, k) => isRight(k)).length;

  return (
    <div className="mt-3">
      {!open ? (
        <button onClick={() => setOpen(true)} data-testid={`conj-quiz-open-${idx}`}
          className="text-xs font-semibold text-emerald-300 hover:text-emerald-200 flex items-center gap-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 rounded-full px-3 py-1.5 transition">
          ✏️ {lang === 'fr' ? "S'entraîner" : 'Practise'}
        </button>
      ) : (
        <div className="bg-slate-950/50 border border-emerald-500/20 rounded-xl p-3" data-testid={`conj-quiz-${idx}`}>
          <div className="text-xs font-semibold text-emerald-200 mb-2">{lang === 'fr' ? 'Complète la forme correcte' : 'Complete the correct form'}</div>
          <div className="space-y-2">
            {rows.map((row, k) => (
              <div key={k} className="flex items-center gap-2">
                <span className="text-white/55 text-sm min-w-[120px] flex-shrink-0">{row[0]} →</span>
                <input value={answers[k] || ''} onChange={(e) => setAnswers((a) => ({ ...a, [k]: e.target.value }))} disabled={checked}
                  data-testid={`conj-quiz-${idx}-input-${k}`} placeholder="…"
                  className={`flex-1 min-w-0 bg-slate-900/70 border rounded-lg px-3 py-1.5 text-sm outline-none transition ${checked ? (isRight(k) ? 'border-emerald-400 text-emerald-100' : 'border-red-400 text-red-100') : 'border-white/15 focus:border-emerald-400/60'}`} />
                {checked && !isRight(k) && <span className="text-xs text-emerald-300 whitespace-nowrap">{row[1]}</span>}
                {checked && isRight(k) && <span className="text-emerald-400 text-sm">✓</span>}
              </div>
            ))}
          </div>
          {!checked ? (
            <button onClick={() => setChecked(true)} data-testid={`conj-quiz-check-${idx}`}
              className="mt-3 text-xs font-bold bg-emerald-500 hover:bg-emerald-400 text-slate-950 rounded-full px-4 py-1.5">
              {lang === 'fr' ? 'Vérifier' : 'Check'}
            </button>
          ) : (
            <div className="mt-3 flex items-center gap-3">
              <span className={`text-sm font-bold ${score === rows.length ? 'text-emerald-300' : 'text-amber-300'}`} data-testid={`conj-quiz-score-${idx}`}>{score}/{rows.length}</span>
              <button onClick={() => { setChecked(false); setAnswers({}); }} data-testid={`conj-quiz-retry-${idx}`}
                className="text-xs font-semibold text-white/70 hover:text-white underline">{lang === 'fr' ? 'Recommencer' : 'Try again'}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Conjugation({ onBack, onComplete, embedded = false, lang = 'fr' }) {
  const fr = lang !== 'en';
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="max-w-3xl mx-auto px-4 py-8" data-testid="conjugation-page">
      {!embedded && (
        <button onClick={onBack} className="text-white/60 hover:text-white text-sm mb-6 flex items-center gap-1">
          <ChevronLeft size={16} />{fr ? 'Retour' : 'Back'}
        </button>
      )}
      <div className="bg-gradient-to-br from-emerald-700 to-emerald-900 rounded-3xl p-6 mb-6">
        <div className="text-xs uppercase tracking-wider text-emerald-200 mb-1">{fr ? 'Espace de référence' : 'Reference space'}</div>
        <h1 className="text-2xl sm:text-3xl font-black mb-2">{fr ? 'Conjugaison' : 'Conjugation'}</h1>
        <p className="text-white/80 text-sm">{fr ? 'Un seul endroit pour maîtriser la conjugaison : les verbes utiles pour parler, avec exercices, et le tableau des verbes irréguliers.' : 'One place to master conjugation: the useful verbs for speaking, with exercises, and the irregular verbs table.'}</p>
      </div>

      {/* Verbes utiles + exercices */}
      <div className="flex items-center gap-2 mb-3">
        <BookOpen size={18} className="text-emerald-300" />
        <h2 className="font-bold text-lg">{fr ? 'Verbes utiles pour parler' : 'Useful verbs for speaking'}</h2>
      </div>
      <div className="space-y-4 mb-8">
        {USEFUL.map((tb, i) => (
          <div key={i} className="bg-emerald-500/5 border border-emerald-500/25 rounded-2xl p-4" data-testid={`useful-verb-${i}`}>
            <div className="font-bold text-emerald-200 text-sm mb-2">{tb.verb}</div>
            <div className="divide-y divide-white/5">
              {tb.rows.map((row, k) => (
                <div key={k} className="flex items-center gap-3 py-1.5">
                  <span className="text-white/55 text-sm min-w-[120px] flex-shrink-0">{row[0]}</span>
                  <span className="font-semibold text-white text-sm flex-1">{row[1]}</span>
                  <button onClick={() => speak(String(row[1]), 'en-US')} className="p-1.5 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/30 flex-shrink-0" aria-label="listen">
                    <Volume2 size={13} className="text-emerald-300" />
                  </button>
                </div>
              ))}
            </div>
            {tb.note && <div className="text-xs text-white/50 mt-2 italic">{tb.note}</div>}
            <VerbQuiz table={tb} idx={i} lang={lang} />
          </div>
        ))}
      </div>

      {/* Tableau des verbes irréguliers — référence, sans exercice */}
      <div className="flex items-center gap-2 mb-3">
        <Table size={18} className="text-yellow-300" />
        <h2 className="font-bold text-lg">{fr ? 'Verbes irréguliers — tableau de référence' : 'Irregular verbs — reference table'}</h2>
      </div>
      <p className="text-white/60 text-sm mb-3">{fr ? 'Pas d’exercice ici : garde ce tableau sous la main pour reconnaître les formes au présent, au passé et au futur.' : 'No exercise here: keep this table handy to recognise present, past and future forms.'}</p>
      <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden" data-testid="irregular-verbs-table">
        <div className="grid grid-cols-4 gap-2 px-3 py-2.5 text-[11px] uppercase tracking-wider text-white/50 bg-slate-900/60 font-semibold">
          <div>{fr ? 'Verbe' : 'Verb'}</div>
          <div>{fr ? 'Présent' : 'Present'}</div>
          <div>{fr ? 'Passé' : 'Past'}</div>
          <div>{fr ? 'Futur' : 'Future'}</div>
        </div>
        <div className="divide-y divide-white/5">
          {IRREGULAR.map((row, i) => (
            <div key={i} className="grid grid-cols-4 gap-2 px-3 py-2 text-sm items-center" data-testid={`irregular-row-${i}`}>
              <div className="text-white/70 text-xs sm:text-sm">{row[0]}</div>
              <div className="font-medium text-white">{row[1]}</div>
              <div className="font-medium text-emerald-200">{row[2]}</div>
              <div className="font-medium text-yellow-100">{row[3]}</div>
            </div>
          ))}
        </div>
      </div>

      {embedded && onComplete && (
        <button onClick={onComplete} data-testid="conjugation-complete"
          className="mt-8 w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold py-3 rounded-xl">
          {fr ? 'Terminer la leçon' : 'Finish the lesson'}
        </button>
      )}
    </motion.div>
  );
}
